/** System tags for todo activity notes (exact names in note bodies). */
const SYSTEM_ACTIVITY_TAGS = ["日历操作", "自定义列表操作"];

function activityTagForListId(listId) {
  return /^\d{8}$/.test(String(listId || ""))
    ? SYSTEM_ACTIVITY_TAGS[0]
    : SYSTEM_ACTIVITY_TAGS[1];
}

function isSystemActivityTag(tag) {
  return SYSTEM_ACTIVITY_TAGS.includes(tag);
}

function summarizeNoteText(text, maxLen) {
  const raw = String(text || "").replace(/\s+/g, " ").trim();
  if (!raw) return "";
  const limit = typeof maxLen === "number" && maxLen > 0 ? maxLen : 40;
  if (raw.length <= limit) return raw;
  return `${raw.slice(0, limit)}…`;
}

function buildActivityBody({ type, todo, listId, meta, t }) {
  const title = (todo && todo.text && String(todo.text).trim()) || t("activity.untitled");
  const tag = activityTagForListId(listId);
  const subtask =
    (meta && meta.subtask && String(meta.subtask).trim()) || t("activity.untitled");
  let line = "";
  if (type === "created") line = t("activity.created", { title });
  else if (type === "completed") line = t("activity.completed", { title });
  else if (type === "uncompleted") line = t("activity.uncompleted", { title });
  else if (type === "postponed") line = t("activity.postponed", { title, to: meta && meta.toLabel });
  else if (type === "moved") {
    line = t("activity.moved", {
      title,
      from: meta && meta.fromLabel,
      to: meta && meta.toLabel,
    });
  } else if (type === "title_changed") {
    line = t("activity.titleChanged", { from: meta && meta.from, to: meta && meta.to });
  } else if (type === "notes_changed") {
    line = t("activity.notesChanged", { summary: meta && meta.summary });
  } else if (type === "notes_cleared") line = t("activity.notesCleared");
  else if (type === "color_changed") {
    line = t("activity.colorChanged", { from: meta && meta.from, to: meta && meta.to });
  } else if (type === "time_changed") {
    line = t("activity.timeChanged", { from: meta && meta.from, to: meta && meta.to });
  } else if (type === "reminder_changed") {
    line = t("activity.reminderChanged", { from: meta && meta.from, to: meta && meta.to });
  } else if (type === "repeat_changed") {
    line = t("activity.repeatChanged", { from: meta && meta.from, to: meta && meta.to });
  } else if (type === "subtask_added") line = t("activity.subtaskAdded", { subtask });
  else if (type === "subtask_renamed") {
    line = t("activity.subtaskRenamed", { from: meta && meta.from, to: meta && meta.to });
  } else if (type === "subtask_completed") line = t("activity.subtaskCompleted", { subtask });
  else if (type === "subtask_uncompleted") line = t("activity.subtaskUncompleted", { subtask });
  else if (type === "subtask_removed") line = t("activity.subtaskRemoved", { subtask });
  else if (type === "list_archived") {
    const name =
      (meta && meta.name && String(meta.name).trim()) || t("activity.untitled");
    line = t("activity.listArchived", { name });
  } else if (type === "list_restored") {
    const name =
      (meta && meta.name && String(meta.name).trim()) || t("activity.untitled");
    line = t("activity.listRestored", { name });
  } else return "";
  const listLevel =
    type === "list_archived" || type === "list_restored"
      ? SYSTEM_ACTIVITY_TAGS[1]
      : tag;
  return `${line}\n#${listLevel}`;
}

function activityEventTarget() {
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") return window;
  return null;
}

function publishTodoActivityUpdated(todoId) {
  const target = activityEventTarget();
  if (!target || !todoId) return;
  const detail = { todoId: String(todoId) };
  const event =
    typeof CustomEvent === "function"
      ? new CustomEvent("todo-activity-updated", { detail })
      : { type: "todo-activity-updated", detail };
  target.dispatchEvent(event);
}

async function logTodoActivity(opts) {
  const { type, todo, listId, meta, t, createNoteFn, onCreated } = opts || {};
  if (!todo || !todo.id) return;
  const body = buildActivityBody({ type, todo, listId, meta, t });
  if (!body) return;
  try {
    const note = await createNoteFn({ body, todoId: todo.id });
    publishTodoActivityUpdated(todo.id);
    if (typeof onCreated === "function") onCreated(note);
  } catch (err) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[todoActivityLog]", err);
    }
  }
}

async function logListActivity(opts) {
  const { type, listId, listName, t, createNoteFn, onCreated } = opts || {};
  if (type !== "list_archived" && type !== "list_restored") return;
  const body = buildActivityBody({
    type,
    todo: null,
    listId: listId || "custom",
    meta: { name: listName },
    t,
  });
  if (!body) return;
  try {
    const note = await createNoteFn({ body });
    if (typeof onCreated === "function") onCreated(note);
  } catch (err) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[todoActivityLog] list", err);
    }
  }
}

function isTodoWriteOk(result) {
  if (Array.isArray(result)) {
    return result.length > 0 && result.every(isTodoWriteOk);
  }
  return !!(result && result.ok === true && result.conflict !== true);
}

/** Delete follow-ups only: a committed conflict merge is not a committed delete. */
function isTodoDeleteWriteOk(result) {
  if (!isTodoWriteOk(result)) return false;
  if (Array.isArray(result)) return result.every((item) => !(item && item.merged === true));
  return !(result && result.merged === true);
}

/**
 * Cascade notes only when this list is loaded and no longer contains the todo.
 * A conflict merge can commit successfully and still put the todo back.
 */
function shouldCascadeTodoNotes(list, todoId) {
  if (todoId == null || todoId === "") return false;
  if (!Array.isArray(list)) return false;
  const id = String(todoId);
  for (let i = 0; i < list.length; i++) {
    const todo = list[i];
    if (todo && todo.id != null && String(todo.id) === id) return false;
  }
  return true;
}

const UNDO_TOAST_MS = 5000;
const cascadeTimers = new Map();
const cascadeGeneration = new Map();

function armTodoNotesCascade(todoId) {
  if (!todoId) return 0;
  const existing = cascadeTimers.get(todoId);
  if (existing) {
    clearTimeout(existing);
    cascadeTimers.delete(todoId);
  }
  const generation = (cascadeGeneration.get(todoId) || 0) + 1;
  cascadeGeneration.set(todoId, generation);
  return generation;
}

function scheduleCascadeDeleteTodoNotes({ todoId, deleteFn, delayMs, generation } = {}) {
  if (!todoId || typeof deleteFn !== "function") return;
  if (generation != null && cascadeGeneration.get(todoId) !== generation) return;
  const existing = cascadeTimers.get(todoId);
  if (existing) {
    clearTimeout(existing);
    cascadeTimers.delete(todoId);
  }
  const gen = generation != null ? generation : cascadeGeneration.get(todoId) || 0;
  const wait = typeof delayMs === "number" && delayMs > 0 ? delayMs : 0;
  const timer = setTimeout(() => {
    if (cascadeTimers.get(todoId) !== timer) return;
    if (cascadeGeneration.get(todoId) !== gen) return;
    cascadeTimers.delete(todoId);
    cascadeDeleteTodoNotes({ todoId, deleteFn });
  }, wait);
  cascadeTimers.set(todoId, timer);
}

function cancelScheduledTodoNotesCascade(todoId) {
  if (!todoId) return;
  const timer = cascadeTimers.get(todoId);
  if (timer) {
    clearTimeout(timer);
    cascadeTimers.delete(todoId);
  }
  cascadeGeneration.set(todoId, (cascadeGeneration.get(todoId) || 0) + 1);
}

function planUndoableNotesCascade(todoId) {
  const generation = armTodoNotesCascade(todoId);
  const startedAt = Date.now();
  return {
    commit({ deleteFn, delayMs } = {}) {
      const budget = typeof delayMs === "number" && delayMs > 0 ? delayMs : 0;
      const remaining = Math.max(0, budget - (Date.now() - startedAt));
      scheduleCascadeDeleteTodoNotes({
        todoId,
        deleteFn,
        delayMs: remaining,
        generation,
      });
    },
  };
}

async function cascadeDeleteTodoNotes({ todoId, deleteFn } = {}) {
  if (!todoId) return;
  try {
    await deleteFn(todoId);
  } catch (err) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[todoActivityLog] cascade", err);
    }
  }
}

module.exports = {
  SYSTEM_ACTIVITY_TAGS,
  activityTagForListId,
  isSystemActivityTag,
  buildActivityBody,
  summarizeNoteText,
  logTodoActivity,
  logListActivity,
  cascadeDeleteTodoNotes,
  isTodoWriteOk,
  isTodoDeleteWriteOk,
  shouldCascadeTodoNotes,
  UNDO_TOAST_MS,
  armTodoNotesCascade,
  scheduleCascadeDeleteTodoNotes,
  cancelScheduledTodoNotesCascade,
  planUndoableNotesCascade,
};
